import React, { useState, useEffect, useRef } from 'react';
import { Pagination, Message } from 'semantic-ui-react';
import 'semantic-ui-css/semantic.min.css';
import './OutboundCampaign.css';
import api from "../../../api";
import StatusBadge from "./StatusBadge";
import CSVSampleDownloadButton from './CSVSampleDownloadButton';
import ManualCustomerEntry from './customers/ManualCustomerEntry';
import CustomerDetailsModalBody from './customers/CustomerDetailsModalBody';
import Modal from '../ChatSetup/components/Modal';
import ConfirmationModal from '../Workspace/Botbuilder/Details/confusedbranch/DataConnect/ConfirmationModal';
import Papa from "papaparse";
import { normalizePhoneNumber } from './scheduleUtils';
import VoicemailRetrySettings from './VoicemailRetrySettings';
import constants from '../../../../src/util_funs/constant';
import { DatatablePageSizeSelect } from '../CompletedChats/DatatablePageSizeSelect';
import { filterCampaignCustomers, useDebouncedValue } from './customers/filterCampaignCustomers';
import DuplicateDataRuleSection, { DUPLICATE_DATA_RULE_MAX_DAYS } from './DuplicateDataRuleSection';
import CallGroupWebhookToggleSection from './CallGroupWebhookToggleSection';
import {
    DUPLICATE_SKIP_NOTICE_DISPLAY_MS,
    formatDuplicateSkippedNotice,
    getDuplicateSkipWindowDays,
} from './duplicateContactsSkipCopy';
import {
    formatCallGroupMemberStatus,
    formatCallGroupPriorityCell,
    formatCallGroupRequiredCell,
    formatCallGroupNameCell,
    callGroupEscalationColor,
    formatOutcomeStatus,
} from './customers/callGroupCustomerFormat';

const phoneRegex = /^\(\d{3}\)\d{3}-\d{4}$/;

const headers = ['Name', 'Phone'];
const sampleData = [
    ['John Doe', '(123)456-7890'],
    ['Jane Smith', '(098)765-4321'],
    ['Mike Johnson', '(112)233-4455'],
    ['Sarah Wilson', '(555)123-4567']
];

/** Shared sizing for rule-section Update actions (reference layout: full-width primary in actions column) */
const CAMPAIGN_DETAILS_RULES_UPDATE_BTN_STYLE = {
    boxSizing: 'border-box',
    minHeight: 40,
    width: '100%',
    borderRadius: 6,
    padding: '10px 16px',
    fontSize: '14px',
    lineHeight: 1.25,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600',
    border: 'none',
    background: '#0066FF',
    color: '#fff',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
};

/** Mapping Guide on Webhook URL card — same look as rule CTAs, not full-width */
const WEBHOOK_MAPPING_GUIDE_BTN_STYLE = {
    ...CAMPAIGN_DETAILS_RULES_UPDATE_BTN_STYLE,
    width: 'auto',
    minWidth: 0,
    flexShrink: 0,
};

const CampaignDetails = ({
    campaign_id,
    user_id,
    onBackToList
}) => {
    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [customersPerPage, setCustomersPerPage] = useState(10);
    const [customerSearch, setCustomerSearch] = useState('');
    const debouncedCustomerSearch = useDebouncedValue(customerSearch);
    const [webhookCopied, setWebhookCopied] = useState(false);
    const [showManualEntry, setShowManualEntry] = useState(false);
    const [showCSVModal, setShowCSVModal] = useState(false);
    const [showWebhookInfoModal, setShowWebhookInfoModal] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [csvError, setCsvError] = useState("");
    const [csvSuccess, setCsvSuccess] = useState("");
    const [csvProcessing, setCsvProcessing] = useState(false);
    const [manualCustomers, setManualCustomers] = useState({ name: '', phone_number: '', manual: true });
    const [errors, setErrors] = useState({});
    const [customersErrors, setCustomersErrors] = useState({});
    const [showOptOutConfirmModal, setShowOptOutConfirmModal] = useState(false);
    const [optOutReenableCustomer, setOptOutReenableCustomer] = useState(null);
    const [showCustomerDetailsModal, setShowCustomerDetailsModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedContactIds, setSelectedContactIds] = useState([]);
    const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
    const [showClearAllConfirmModal, setShowClearAllConfirmModal] = useState(false);
    const [customersRefreshing, setCustomersRefreshing] = useState(false);
    const [rulesDelayDays, setRulesDelayDays] = useState(0);
    const [rulesDelaySaving, setRulesDelaySaving] = useState(false);
    const [retrySettings, setRetrySettings] = useState({
        voicemail_retry_enabled: false,
        voicemail_retry_delay_days: 1,
        voicemail_retry_max_attempts: 1,
        voicemail_retry_apply_to_send_now: false
    });
    const [retrySaving, setRetrySaving] = useState(false);
    const [dupRuleEnabled, setDupRuleEnabled] = useState(false);
    const [dupRuleDays, setDupRuleDays] = useState('');
    const [dupRuleSaving, setDupRuleSaving] = useState(false);
    const [dupRuleDaysError, setDupRuleDaysError] = useState('');
    const [callGroupWebhookAccept, setCallGroupWebhookAccept] = useState(false);
    const [callGroupWebhookSaving, setCallGroupWebhookSaving] = useState(false);
    const fileInputRef = useRef(null);
    const messageRef = useRef(null);
    const selectAllCheckboxRef = useRef(null);
    /** Bumped on every local customer edit; a refresh that started before an edit drops its stale response. */
    const customersEditsRef = useRef(0);
    /** True while a Clear all request is running, so a second confirm cannot send another destructive request. */
    const clearAllInFlightRef = useRef(false);
    const messageTimerRef = useRef(null);
    const [timeframesExpanded, setTimeframesExpanded] = useState(false);

    /** Shows the page banner, scrolls it into view and hides it after `ms`. A newer banner replaces (and re-times) this one. */
    const showTimedMessage = (type, text, ms = 7000) => {
        clearTimeout(messageTimerRef.current);
        setMessage({ type, text });
        setTimeout(() => {
            if (messageRef.current) {
                messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
            }
        }, 150);
        messageTimerRef.current = setTimeout(() => setMessage(null), ms);
    };

    useEffect(() => () => clearTimeout(messageTimerRef.current), []);

    function transformLaravelErrors(errors) {
        const formErrors = {};
        const nestedCustomerErrors = [];

        Object.keys(errors).forEach((key) => {
            const messages = Array.isArray(errors[key])
                ? errors[key].join(', ')
                : errors[key];

            if (key.startsWith('customers.')) {
                const match = key.match(/^customers\.(\d+)\.(.+)$/);
                if (match) {
                    const index = parseInt(match[1], 10);
                    const field = match[2];

                    // Remove dynamic prefix: customers.{index}.
                    const cleanedMessage = messages.replace(
                        new RegExp(`customers\\.${index}\\.`, 'g'),
                        ''
                    );

                    nestedCustomerErrors.push({
                        index,
                        field,
                        message: cleanedMessage
                    });
                }
            } else {
                formErrors[key] = messages;
            }
        });

        return { formErrors, nestedCustomerErrors };
    }

    const handleCopyWebhook = async () => {
        try {
            if (campaign?.webhook_url) {
                await navigator.clipboard.writeText(campaign.webhook_url);
                setWebhookCopied(true);
                setTimeout(() => setWebhookCopied(false), 5000);
            }
        } catch (e) {
            console.error('Failed to copy webhook URL:', e);
        }
    };

    useEffect(() => {
        if (campaign_id) {
            fetchCampaignDetails();
        }
    }, [campaign_id]);

    useEffect(() => {
        if (campaign?.type === 'triggered') {
            setRulesDelayDays(Number(campaign.webhook_queue_delay_days) || 0);
        }
    }, [campaign?.id, campaign?.type, campaign?.webhook_queue_delay_days]);

    useEffect(() => {
        if (campaign) {
            setRetrySettings({
                voicemail_retry_enabled: campaign.voicemail_retry_enabled || false,
                voicemail_retry_delay_days: campaign.voicemail_retry_delay_days || 1,
                voicemail_retry_max_attempts: campaign.voicemail_retry_max_attempts || 1,
                voicemail_retry_apply_to_send_now: campaign.voicemail_retry_apply_to_send_now || false
            });
        }
    }, [campaign?.id, campaign?.voicemail_retry_enabled, campaign?.voicemail_retry_delay_days, campaign?.voicemail_retry_max_attempts, campaign?.voicemail_retry_apply_to_send_now]);

    useEffect(() => {
        if (!campaign) return;
        setDupRuleEnabled(!!campaign.duplicate_data_rule_enabled);
        const d = campaign.duplicate_data_rule_days;
        setDupRuleDays(
            campaign.duplicate_data_rule_enabled &&
                d != null &&
                d !== ''
                ? Number(d)
                : ''
        );
        setDupRuleDaysError('');
    }, [campaign?.id, campaign?.duplicate_data_rule_enabled, campaign?.duplicate_data_rule_days]);

    useEffect(() => {
        if (!campaign) return;
        const m = campaign.call_group_mode;
        setCallGroupWebhookAccept(m === 'webhook_inline' || m === 'saved_template');
    }, [campaign?.id, campaign?.call_group_mode]);

    useEffect(() => {
        const allowed = new Set(customers.filter((c) => c?.id).map((c) => c.id));
        setSelectedContactIds((prev) => prev.filter((id) => allowed.has(id)));
    }, [customers]);

    const formatStatus = (status) => {
        if (!status) return '';
        if (status === 'approved' || status === 'twilio-approved') return 'Completed';
        if (status === 'non_marketing') return 'General Non-Marketing';
        return status
            .replace(/[-_]/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };
    const getConsentTypeLabel = (value) =>
        constants.CONSENT_TYPE_OPTIONS.find(o => o.value === value)?.text
        || (value ? formatStatus(value) : "N/A");

    const isCampaignCompleted = (status) => {
        return status === 'completed';
    };

    const canEditWebhookQueueDelay = () =>
        !!campaign && String(campaign.type || '').toLowerCase() === 'triggered';

    const formatScheduleDateForApi = (d) => {
        if (!d) return null;
        if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d.trim())) return d.trim();
        try {
            const date = new Date(d);
            if (Number.isNaN(date.getTime())) return null;
            return date.toISOString().split('T')[0];
        } catch {
            return null;
        }
    };

    const handleSaveWebhookQueueDelay = async () => {
        if (!campaign || !canEditWebhookQueueDelay()) return;
        const delayVal = Math.min(365, Math.max(0, parseInt(rulesDelayDays, 10) || 0));
        setRulesDelaySaving(true);
        try {
            const res = await api.outbound.updateCampaignWebhookQueueDelay(campaign_id, delayVal);
            const scrollBanner = () => {
                setTimeout(() => {
                    if (messageRef.current) {
                        messageRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest',
                        });
                    }
                }, 150);
            };
            if (res.status) {
                setMessage({ type: 'success', text: res.message || 'Webhook queue delay updated.' });
                scrollBanner();
                if (res.data) {
                    setCampaign(res.data);
                    setCustomers(res.data.customers || []);
                } else {
                    setCampaign((prev) =>
                        prev ? { ...prev, webhook_queue_delay_days: delayVal } : prev
                    );
                }
            } else {
                setMessage({
                    type: 'error',
                    text: res.message || 'Could not update webhook queue delay.',
                });
                scrollBanner();
            }
            setTimeout(() => setMessage(null), 7000);
        } catch (err) {
            console.error('Update webhook queue delay:', err);
            setMessage({
                type: 'error',
                text: err.response?.data?.message || err.message || 'Could not update webhook queue delay.',
            });
            setTimeout(() => {
                if (messageRef.current) {
                    messageRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                    });
                }
            }, 150);
            setTimeout(() => setMessage(null), 7000);
        } finally {
            setRulesDelaySaving(false);
        }
    };

    const handleRetrySettingsChange = (field, value) => {
        setRetrySettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveRetrySettings = async () => {
        if (!campaign) return;
        const sch = campaign.schedule || {};
        setRetrySaving(true);
        try {
            const payload = {
                workspace_id: campaign.workspace_id,
                outbound_twilio_number_id: campaign.outbound_twilio_number_id,
                user_id: parseInt(user_id, 10),
                name: campaign.name,
                consent_type: campaign.consent_type,
                consent_notes: campaign.consent_notes || '',
                type: campaign.type,
                send_type: campaign.send_type,
                schedule_type: sch.schedule_type || null,
                timezone: sch.timezone || '',
                start_date: formatScheduleDateForApi(sch.start_date),
                end_date: formatScheduleDateForApi(sch.end_date),
                start_time: sch.start_time || null,
                end_time: sch.end_time || null,
                days_of_week: sch.days || [],
                webhook_queue_delay_days: campaign.webhook_queue_delay_days || 0,
                voicemail_retry_enabled: retrySettings.voicemail_retry_enabled,
                voicemail_retry_delay_days: retrySettings.voicemail_retry_delay_days,
                voicemail_retry_max_attempts: retrySettings.voicemail_retry_max_attempts,
                voicemail_retry_apply_to_send_now: retrySettings.voicemail_retry_apply_to_send_now
            };
            const res = await api.outbound.updateCampaign(campaign_id, payload);
            const scrollBanner = () => {
                setTimeout(() => {
                    if (messageRef.current) {
                        messageRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest',
                        });
                    }
                }, 150);
            };
            if (res.status) {
                setMessage({ type: 'success', text: res.message || 'Voicemail retry settings updated.' });
                scrollBanner();
                if (res.data) {
                    setCampaign(res.data);
                    setCustomers(res.data.customers || []);
                }
            } else {
                setMessage({
                    type: 'error',
                    text: res.message || 'Could not update voicemail retry settings.',
                });
                scrollBanner();
            }
            setTimeout(() => setMessage(null), 7000);
        } catch (err) {
            console.error('Update voicemail retry settings:', err);
            setMessage({
                type: 'error',
                text: err.response?.data?.message || err.message || 'Could not update voicemail retry settings.',
            });
            setTimeout(() => {
                if (messageRef.current) {
                    messageRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                    });
                }
            }, 150);
            setTimeout(() => setMessage(null), 7000);
        } finally {
            setRetrySaving(false);
        }
    };


    const handleSaveCallGroupWebhookMode = async () => {
        if (!campaign_id || !campaign) return;
        if (isCampaignCompleted(campaign.status)) return;

        const sch = campaign.schedule || {};
        const nextMode = (() => {
            if (!callGroupWebhookAccept) return 'individual';
            if (campaign.call_group_mode === 'saved_template') return 'saved_template';
            return 'webhook_inline';
        })();

        setCallGroupWebhookSaving(true);
        try {
            const payload = {
                workspace_id: campaign.workspace_id,
                outbound_twilio_number_id: campaign.outbound_twilio_number_id,
                user_id: parseInt(user_id, 10),
                name: campaign.name,
                consent_type: campaign.consent_type,
                consent_notes: campaign.consent_notes || '',
                type: campaign.type,
                send_type: campaign.send_type,
                schedule_type: sch.schedule_type || null,
                timezone: sch.timezone || '',
                start_date: formatScheduleDateForApi(sch.start_date),
                end_date: formatScheduleDateForApi(sch.end_date),
                start_time: sch.start_time || null,
                end_time: sch.end_time || null,
                days_of_week: sch.days || [],
                webhook_queue_delay_days: campaign.webhook_queue_delay_days || 0,
                voicemail_retry_enabled: campaign.voicemail_retry_enabled,
                voicemail_retry_delay_days: campaign.voicemail_retry_delay_days,
                voicemail_retry_max_attempts: campaign.voicemail_retry_max_attempts,
                voicemail_retry_apply_to_send_now: campaign.voicemail_retry_apply_to_send_now,
                call_group_mode: nextMode,
            };
            const res = await api.outbound.updateCampaign(campaign_id, payload);
            const scrollBanner = () => {
                setTimeout(() => {
                    if (messageRef.current) {
                        messageRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest',
                        });
                    }
                }, 150);
            };
            if (res.status) {
                setMessage({
                    type: 'success',
                    text: res.message || 'Call group webhook setting updated.',
                });
                scrollBanner();
                if (res.data) {
                    setCampaign(res.data);
                    setCustomers(res.data.customers || []);
                }
            } else {
                setMessage({
                    type: 'error',
                    text: res.message || 'Could not update call group setting.',
                });
                scrollBanner();
            }
            setTimeout(() => setMessage(null), 7000);
        } catch (err) {
            console.error('Update call group webhook mode:', err);
            setMessage({
                type: 'error',
                text: err.response?.data?.message || err.message || 'Could not update call group setting.',
            });
            setTimeout(() => {
                if (messageRef.current) {
                    messageRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                    });
                }
            }, 150);
            setTimeout(() => setMessage(null), 7000);
        } finally {
            setCallGroupWebhookSaving(false);
        }
    };

    const handleSaveDuplicateDataRule = async () => {
        setDupRuleDaysError('');
        if (dupRuleEnabled) {
            if (dupRuleDays === '' || dupRuleDays === null || dupRuleDays === undefined) {
                setDupRuleDaysError('Number of days is required when this rule is enabled.');
                return;
            }
            const d = parseInt(dupRuleDays, 10);
            if (Number.isNaN(d) || d < 1 || d > DUPLICATE_DATA_RULE_MAX_DAYS) {
                setDupRuleDaysError(
                    `Enter a whole number of days from 1 to ${DUPLICATE_DATA_RULE_MAX_DAYS}.`
                );
                return;
            }
        }
        if (!campaign_id) return;

        const daysVal = dupRuleEnabled
            ? Math.min(
                  DUPLICATE_DATA_RULE_MAX_DAYS,
                  Math.max(1, parseInt(dupRuleDays, 10))
              )
            : null;

        setDupRuleSaving(true);
        try {
            const res = await api.outbound.updateCampaignDuplicateDataRule(
                campaign_id,
                dupRuleEnabled,
                daysVal
            );
            const scrollBanner = () => {
                setTimeout(() => {
                    if (messageRef.current) {
                        messageRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest',
                        });
                    }
                }, 150);
            };
            if (res.status) {
                setMessage({
                    type: 'success',
                    text: res.message || 'Duplicate data rule updated.',
                });
                scrollBanner();
                if (res.data) {
                    setCampaign(res.data);
                    setCustomers(res.data.customers || []);
                } else {
                    setCampaign((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  duplicate_data_rule_enabled: dupRuleEnabled,
                                  duplicate_data_rule_days: daysVal,
                              }
                            : prev
                    );
                }
            } else {
                setMessage({
                    type: 'error',
                    text: res.message || 'Could not update duplicate data rule.',
                });
                scrollBanner();
            }
            setTimeout(() => setMessage(null), DUPLICATE_SKIP_NOTICE_DISPLAY_MS);
        } catch (err) {
            console.error('Update duplicate data rule:', err);
            setMessage({
                type: 'error',
                text:
                    err.response?.data?.message ||
                    err.message ||
                    'Could not update duplicate data rule.',
            });
            setTimeout(() => {
                if (messageRef.current) {
                    messageRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                    });
                }
            }, 150);
            setTimeout(() => setMessage(null), DUPLICATE_SKIP_NOTICE_DISPLAY_MS);
        } finally {
            setDupRuleSaving(false);
        }
    };

    /** Browser UTC offset as "+HH:MM", the format the campaign API expects for its timezone param. */
    const clientTimeZoneOffset = () => {
        const u = new Date().toString().match(/([-\+][0-9]+)\s/)[1];
        return u.substring(0, 3) + ":" + u.substring(3, u.length);
    };

    const fetchCampaignDetails = async () => {
        setLoading(true);
        setMessage(null);

        try {
            const response = await api.outbound.getCampaign(campaign_id, clientTimeZoneOffset());

            if (response.status) {
                setCampaign(response.data);
                setCustomers(response.data.customers || []);
                return true;
            }
            setMessage({ type: 'error', text: response.message || 'Failed to fetch campaign details' });
            return false;
        } catch (error) {
            console.error('Error fetching campaign details:', error);
            setMessage({ type: 'error', text: 'An error occurred while fetching campaign details' });
            return false;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Re-reads customer rows and call statuses in place (no full-page loader, no other campaign state touched).
     * Read-only. Keeps the current page when it still exists, otherwise shows the last page, and clears bulk
     * selection so rows that changed underneath the user cannot be deleted by accident.
     *
     * @returns {Promise<{ok: boolean, waiting: number|null}>} ok=false when the refresh failed (banner already shown);
     *   waiting is the fresh server count, or null when the response was skipped or carried no count.
     */
    const refreshCustomers = async () => {
        if (customersRefreshing) return { ok: true, waiting: null };
        setCustomersRefreshing(true);
        const editsAtStart = customersEditsRef.current;

        try {
            const response = await api.outbound.getCampaign(campaign_id, clientTimeZoneOffset());
            if (!response?.status) {
                showTimedMessage('error', response?.message || 'Failed to refresh customers', 5000);
                return { ok: false, waiting: null };
            }
            // The user edited a row (e.g. opt-out toggle) while this request was in flight, or a Clear all ran; the
            // snapshot predates that, so keep the local rows rather than overwrite them. The next refresh picks up the
            // server state.
            if (customersEditsRef.current !== editsAtStart) return { ok: true, waiting: null };

            const refreshed = response.data?.customers || [];
            setCustomers(refreshed);
            // Only take the count when the server sent one; a response without it must not read as "0 waiting".
            const waiting = response.data?.waiting_customers_count;
            const hasCount = waiting !== undefined && waiting !== null;
            if (hasCount) {
                setCampaign((prev) => (prev ? { ...prev, waiting_customers_count: waiting } : prev));
            }
            setSelectedContactIds([]);
            return { ok: true, waiting: hasCount ? Number(waiting) : null };
        } catch (error) {
            console.error('Error refreshing customers:', error);
            showTimedMessage('error', 'An error occurred while refreshing customers', 5000);
            return { ok: false, waiting: null };
        } finally {
            setCustomersRefreshing(false);
        }
    };

    const handlePageChange = (e, { activePage }) => {
        setCurrentPage(activePage);
    };


    const displayScheduleType = (schedule_type) => {
        switch (schedule_type) {
            case 'one_time':
                return 'One Time';
            case 'ongoing':
                return 'Ongoing';
            default:
                return schedule_type || 'N/A';
        }
    };

    const getTypeBadge = (type) => {
        if (!type) return null;

        const normalizedType = type.toLowerCase();

        const capitalizedText = normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1);

        let icon = null;
        if (normalizedType === 'voice') {
            icon = <i className="la la-phone campaign-info-card__type-pill-icon" aria-hidden="true" />;
        } else if (normalizedType === 'sms') {
            icon = <i className="la la-commenting campaign-info-card__type-pill-icon" aria-hidden="true" />;
        } else if (normalizedType === 'triggered') {
            icon = <i className="la la-bolt campaign-info-card__type-pill-icon" aria-hidden="true" />;
        }

        return (
            <span className="campaign-info-card__type-pill">
                {icon}
                {capitalizedText}
            </span>
        );
    };

    const getSendTypeDisplay = (sendType) => {
        switch (sendType) {
            case 'now':
                return 'Send Now';
            case 'scheduled':
                return 'Scheduled';
            default:
                if (!sendType) return 'N/A';
                return sendType
                    .replace(/[-_]/g, ' ')
                    .replace(/\b\w/g, (char) => char.toUpperCase());
        }
    };

    // Calculate pagination for customers
    const filteredCustomers = filterCampaignCustomers(customers, debouncedCustomerSearch);
    const totalCustomers = filteredCustomers.length;
    const totalPages = Math.max(1, Math.ceil(totalCustomers / customersPerPage) || 1);
    const activePage = Math.min(currentPage, totalPages);
    const startIndex = (activePage - 1) * customersPerPage;
    const endIndex = startIndex + customersPerPage;
    const currentCustomers = filteredCustomers.slice(startIndex, endIndex);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedCustomerSearch, customersPerPage]);

    useEffect(() => {
        setCurrentPage((p) => Math.min(p, totalPages));
    }, [totalPages]);

    /** Same visibility as single-row delete (trash): only triggered campaigns. */
    const showBulkDelete = campaign?.type === 'triggered';
    /** Same enablement as clickable trash: triggered and campaign not completed. */
    const bulkDeleteEnabled =
        campaign?.type === 'triggered' && !isCampaignCompleted(campaign?.status);

    const contactHasServerId = (c) => !!c?.id;
    const bulkSelectableContacts = currentCustomers.filter(contactHasServerId);
    const allBulkRowsSelectedOnPage =
        bulkSelectableContacts.length > 0 &&
        bulkSelectableContacts.every((c) => selectedContactIds.includes(c.id));

    useEffect(() => {
        const el = selectAllCheckboxRef.current;
        if (!el || !showBulkDelete) return;
        const selectedOnPage = bulkSelectableContacts.filter((c) => selectedContactIds.includes(c.id));
        el.indeterminate =
            selectedOnPage.length > 0 && selectedOnPage.length < bulkSelectableContacts.length;
    }, [showBulkDelete, bulkSelectableContacts, selectedContactIds]);

    useEffect(() => {
        if (campaign?.type === 'triggered' && isCampaignCompleted(campaign?.status)) {
            setSelectedContactIds([]);
        }
    }, [campaign?.type, campaign?.status]);

    const toggleBulkCheckbox = (customer) => {
        if (!bulkDeleteEnabled || !contactHasServerId(customer)) return;
        setSelectedContactIds((prev) =>
            prev.includes(customer.id)
                ? prev.filter((id) => id !== customer.id)
                : [...prev, customer.id]
        );
    };

    const toggleSelectAllBulkCheckboxes = () => {
        if (!bulkDeleteEnabled) return;
        const ids = bulkSelectableContacts.map((c) => c.id);
        setSelectedContactIds((prev) => {
            const allSelected = ids.length > 0 && ids.every((id) => prev.includes(id));
            if (allSelected) return prev.filter((id) => !ids.includes(id));
            return [...new Set([...prev, ...ids])];
        });
    };

    /** Show call-group columns for webhook / saved-template triggered campaigns (API returns batch + member status). */
    const showCallGroupColumns =
        campaign?.call_group_mode === 'webhook_inline' || campaign?.call_group_mode === 'saved_template';

    const customersTableColSpan = (showBulkDelete ? 8 : 7) + (showCallGroupColumns ? 4 : 0);

    const hasTimeframes =
        !!campaign &&
        campaign.enable_per_day_timeframes &&
        campaign.day_timeframes &&
        Object.keys(campaign.day_timeframes).length > 0;

    const dayScheduleBadgeLabel = (day) => {
        const abbrevs = {
            Monday: 'MON',
            Tuesday: 'TUE',
            Wednesday: 'WED',
            Thursday: 'THU',
            Friday: 'FRI',
            Saturday: 'SAT',
            Sunday: 'SUN',
        };
        return abbrevs[day] || String(day).slice(0, 3).toUpperCase();
    };

    const executionStatusBadgeClass = (status) => {
        const ex = String(status || '').toLowerCase();
        if (ex === 'called' || ex === 'answered') return 'badge badge-success';
        if (ex === 'failed') return 'badge badge-danger';
        return 'badge badge-secondary';
    };

    /** DNC / compliance list names when API returns them (shape varies). */
    const dncListTags = (() => {
        if (!campaign) return [];
        const raw =
            campaign.dnc_lists ??
            campaign.dnc_list_names ??
            campaign.attached_dnc_lists ??
            campaign.dnc_list ??
            campaign.compliance_dnc_lists ??
            campaign.dnc_lists_attached;
        if (raw == null) return [];
        if (Array.isArray(raw)) {
            return raw
                .map((item) =>
                    typeof item === 'string'
                        ? item.trim()
                        : String(
                              item?.name ??
                                  item?.label ??
                                  item?.title ??
                                  (item?.id != null ? item.id : '')
                          ).trim()
                )
                .filter(Boolean);
        }
        if (typeof raw === 'string' && raw.trim()) return [raw.trim()];
        if (typeof raw === 'object') {
            const name = raw.name || raw.label || raw.title;
            if (name) return [String(name)];
        }
        return [];
    })();

    const bulkDeleteDisabled =
        !bulkDeleteEnabled || selectedContactIds.length === 0;

    /** Not-yet-dialed contacts across all pages (server count), what "Clear all" removes. */
    const waitingCount = Number(campaign?.waiting_customers_count) || 0;
    const clearAllDisabled = !bulkDeleteEnabled || waitingCount === 0;

    const phoneFormat = (p) => {
        let d = String(p ?? '').replace(/\D/g, '');
        // NANP often stored as 11 digits with leading country code 1 — strip before (000)000-0000
        if (d.length === 11 && d[0] === '1') d = d.slice(1);
        if (d.length <= 3) return d;
        if (d.length <= 6) return `(${d.slice(0, 3)})${d.slice(3)}`;
        return `(${d.slice(0, 3)})${d.slice(3, 6)}-${d.slice(6, 10)}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'N/A';
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = date.getFullYear();
            return `${month}/${day}/${year}`;
        } catch (error) {
            return 'N/A';
        }
    };

    /** Schedule card: e.g. May 13, 2026 */
    const formatScheduleDisplayDate = (value) => {
        if (value == null || value === '') return '—';
        const raw = String(value).trim();
        const tryDate = new Date(raw);
        if (!isNaN(tryDate.getTime())) {
            return tryDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        }
        if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
            const d = new Date(`${raw.slice(0, 10)}T12:00:00`);
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            }
        }
        return raw;
    };

    /** Schedule card: 24h HH:mm → 12h with AM/PM; passthrough if already contains AM/PM */
    const formatScheduleTime12h = (value) => {
        if (value == null || value === '') return '—';
        const s = String(value).trim();
        if (/am|pm/i.test(s)) return s;
        const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
        if (!m) return s;
        let h = parseInt(m[1], 10);
        const min = m[2];
        const ap = h >= 12 ? 'PM' : 'AM';
        const h24 = h;
        if (h24 === 0) {
            h = 12;
        } else if (h24 > 12) {
            h = h24 - 12;
        }
        return `${h}:${min} ${ap}`;
    };

    function validateName(name) {
        const errors = [];
        if (!name || name.trim() === "") {
            errors.push("Name is required.");
        } else if (name.length < 2) {
            errors.push("The name field must be at least 2 characters.");
        } else if (name.length > 50) {
            errors.push("Name cannot exceed 50 characters.");
        }
        return errors;
    }

    function validateCSVHeaders(headers, requiredHeaders) {
        const errors = [];
        if (!headers || headers.length === 0) {
            errors.push("CSV has no headers.");
            return errors;
        }
        const cleanHeaders = headers.map(h => h.trim().toLowerCase());

        // Require name + phone columns, but allow additional (dynamic) columns.
        const missing = requiredHeaders.filter(h => !cleanHeaders.includes(h.toLowerCase()));
        if (missing.length > 0) {
            errors.push(`CSV is missing required column(s): ${missing.join(", ")}.`);
            return errors;
        }
        return errors;
    }

    const validateCustomer = () => {
        const newErrors = {};
        const name = manualCustomers.name ? manualCustomers.name.trim() : "";
        const phone = manualCustomers.phone_number;
        const nameErrors = validateName(name);
        if (nameErrors.length > 0) {
            newErrors.name = nameErrors[0];
        }
        if (!phone) {
            newErrors.phone_number = "Phone number is required.";
        } else if (!phoneRegex.test(phone)) {
            newErrors.phone_number = "Valid phone number required ((000)000-0000)";
        }
        // const isDuplicate = customers.some((c) => c.phone_number === phone);
        // if (isDuplicate) {
        //     newErrors.phone_number = "Duplicate phone number.";
        // }
        return newErrors;
    };

    const handleChange = (field, value) => {
        setErrors({});
        if (field === "phone_number") value = phoneFormat(value);
        setManualCustomers(prev => ({ ...prev, [field]: value }));
    };

    const handleAddCustomer = async () => {
        const validationErrors = validateCustomer();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        setErrors({});
        try {
            const encryptedId = btoa(`${user_id}/${campaign_id}`);
            const response = await api.outbound.storeTriggeredCampaignCustomers(
                encryptedId,
                [{
                    name: manualCustomers.name.trim(),
                    phone_number: manualCustomers.phone_number,
                    manual: true,
                }],
                { skipWebhookQueueDelay: true }
            );

            if (response?.status) {
                if (response.inserted_count > 0) {
                    setManualCustomers({ name: '', phone_number: '', manual: true });
                    await fetchCampaignDetails();
                    setMessage({ type: 'success', text: 'Customer added successfully!' });

                    setTimeout(() => {
                        if (messageRef.current) {
                            messageRef.current.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                                inline: 'nearest'
                            });
                        }
                    }, 150);

                    setTimeout(() => setMessage(null), 5000);
                } else {
                    const blocked = response.blocked_customers || [];
                    const windowDays = getDuplicateSkipWindowDays(campaign, blocked);
                    const notice = formatDuplicateSkippedNotice(blocked.length, windowDays);
                    setMessage({
                        type: 'error',
                        text: notice || 'Customer could not be added.',
                    });
                    setTimeout(() => setMessage(null), DUPLICATE_SKIP_NOTICE_DISPLAY_MS);
                }
            } else {
                // Backend validation errors
                const transformedErrors = {};
                if (response?.errors) {
                    Object.keys(response.errors).forEach((key) => {
                        const match = key.match(/^customers\.0\.(.+)$/);
                        if (match) {
                            const field = match[1];
                            let errorMessage = Array.isArray(response.errors[key])
                                ? response.errors[key][0]
                                : response.errors[key];
                            errorMessage = typeof errorMessage === 'string' ? errorMessage : String(errorMessage);
                            errorMessage = errorMessage.replace(/customers\.0\./g, '');
                            transformedErrors[field] = errorMessage;
                        }
                    });
                }

                // If no transformed errors, use the message as fallback (but don't show general validation error message)
                if (Object.keys(transformedErrors).length === 0) {
                    // Only show message if it's not the generic "Validation errors" message
                    if (response?.message && response.message !== 'Validation errors') {
                        transformedErrors.phone_number = response.message;
                    } else {
                        transformedErrors.phone_number = 'Failed to add customer';
                    }
                }

                setErrors(transformedErrors);
                // Don't show general message for validation errors - field-specific errors are shown
                // Auto-remove error after 5 seconds
                setTimeout(() => {
                    setErrors({});
                }, 5000);
            }
        } catch (error) {
            setErrors({ phone_number: error.message || 'Network error occurred' });
            // Auto-remove error after 5 seconds
            setTimeout(() => {
                setErrors({});
            }, 5000);
        } finally {
            setLoading(false);
        }
    };

    const clearCSVInputAndErrors = () => {
        setCsvFile(null);
        setCsvError("");
        setCsvSuccess("");
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const autoCloseModalOnError = () => {
        setTimeout(() => {
            clearCSVInputAndErrors();
        }, 3000);
    };

    const handleCSVUpload = () => {
        if (!csvFile) {
            setCsvError(["Please select a CSV file."]);
            autoCloseModalOnError();
            return;
        }

        const isCSV = csvFile.type === "text/csv" || csvFile.name.endsWith(".csv");
        if (!isCSV) {
            setCsvError(["Please upload a valid CSV file (.csv)"]);
            autoCloseModalOnError();
            return;
        }

        setCsvProcessing(true);
        setCsvError("");
        setCsvSuccess("");

        Papa.parse(csvFile, {
            header: true,
            skipEmptyLines: true,
            encoding: 'UTF-8',
            transformHeader: function (header) {
                return header.trim();
            },
            complete: async function (results) {
                try {
                    const headers = results.meta.fields || [];
                    const headerErrors = validateCSVHeaders(headers, ["name", "phone"]);
                    if (headerErrors.length > 0) {
                        setCsvError(headerErrors);
                        setCsvProcessing(false);
                        // Auto-remove errors after 5 seconds
                        setTimeout(() => {
                            setCsvError("");
                        }, 5000);
                        return;
                    }
                    const parsed = results.data;
                    if (!parsed || parsed.length === 0) {
                        setCsvError(["CSV file is empty. Please add at least one customer."]);
                        setCsvProcessing(false);
                        // Auto-remove errors after 5 seconds
                        setTimeout(() => {
                            setCsvError("");
                        }, 5000);
                        return;
                    }
                    const validEntries = [];
                    const invalidEntries = [];

                    const getRowValue = (row, key) => {
                        const k = Object.keys(row || {}).find((c) => String(c).trim().toLowerCase() === key);
                        return k != null ? row[k] : undefined;
                    };

                    parsed.forEach((row, index) => {
                        const name = getRowValue(row, 'name')?.trim();
                        const phone_number = normalizePhoneNumber(getRowValue(row, 'phone'));
                        const nameErrors = validateName(name);
                        const errors = [...nameErrors];
                        const strictPattern = /^\(\d{3}\)\d{3}-\d{4}$/;
                        if (!phone_number) {
                            errors.push('Phone number is missing or blank.');
                        } else if (phone_number.length !== 10) {
                            errors.push(`Phone number must be 10 digits.`);
                        }
                        // else if (!strictPattern.test(row.phone.trim())) {
                        //     errors.push(`The phone number must be in the format (123)456-7890.`);
                        // }

                        //         if (errors.length === 0) {
                        // const isDuplicate = customers.some(c =>
                        //                 normalizePhoneNumber(c.phone_number) === phone_number
                        //             );
                        //             if (isDuplicate) {
                        //                 errors.push(`${row.phone} Duplicate phone number.`);
                        //             }
                        //         }

                        //                 if (errors.length === 0) {
                        // const isDuplicateInCSV = validEntries.slice(0, index).some(existing =>
                        //                         normalizePhoneNumber(existing.phone_number) === phone_number
                        //                     );
                        //                     if (isDuplicateInCSV) {
                        //                         errors.push(`${row.phone} Duplicate phone number.`);
                        //                     }
                        //                 }

                        if (errors.length > 0) {
                            invalidEntries.push({
                                row: index + 2,
                                name: name || "Blank",
                                phone_number: getRowValue(row, 'phone') || "Blank",
                                errors,
                            });
                        } else {
                            // Treat any additional CSV columns as dynamic variables; key = header with spaces removed (e.g. "COMPANY Name" -> "COMPANYName")
                            const dynamic_variables = {};
                            Object.keys(row || {}).forEach((key) => {
                                if (!key) return;
                                const trimmedKey = String(key).trim();
                                if (!trimmedKey) return;
                                const keyNoSpaces = trimmedKey.replace(/\s+/g, '');
                                if (!keyNoSpaces) return;
                                if (keyNoSpaces.toLowerCase() === "name" || keyNoSpaces.toLowerCase() === "phone") return;
                                const value = row[key];
                                if (value === undefined || value === null) return;
                                const str = String(value).trim();
                                if (str === "") return;
                                dynamic_variables[keyNoSpaces] = str;
                            });

                            validEntries.push({
                                name,
                                phone_number: getRowValue(row, 'phone'),
                                manual: false,
                                ...(Object.keys(dynamic_variables).length > 0 ? { dynamic_variables } : {})
                            });
                        }
                    });

                    // Show invalid entries errors first
                    if (invalidEntries.length > 0) {
                        const allErrors = [];
                        invalidEntries.forEach(invalid => {
                            allErrors.push(`Row ${invalid.row}: ${invalid.errors.join(', ')}`);
                        });
                        setCsvError(allErrors);
                        setCsvProcessing(false);
                        // Auto-remove errors after 5 seconds
                        setTimeout(() => {
                            setCsvError("");
                        }, 5000);
                        return; // Don't proceed if there are validation errors
                    }

                    // If we have valid entries, send them to the backend
                    if (validEntries.length > 0) {
                        try {
                            // Format customers for API (ensure phone_number is in correct format)
                            const customersToAdd = validEntries.map(entry => ({
                                name: entry.name.trim(),
                                phone_number: entry.phone_number,
                                ...(entry.dynamic_variables ? { dynamic_variables: entry.dynamic_variables } : {})
                            }));

                            const encryptedId = btoa(`${user_id}/${campaign_id}`);
                            const response = await api.outbound.storeTriggeredCampaignCustomers(
                                encryptedId,
                                customersToAdd,
                                { skipWebhookQueueDelay: true }
                            );

                            if (response?.status) {
                                const blocked = response.blocked_customers || [];
                                const windowDays = getDuplicateSkipWindowDays(campaign, blocked);

                                if (response.inserted_count > 0) {
                                    setCsvSuccess(`${response.inserted_count} customer(s) added successfully.`);
                                    let successMsg = `${response.inserted_count} customer(s) added successfully!`;
                                    if (blocked.length > 0) {
                                        successMsg += ` ${formatDuplicateSkippedNotice(blocked.length, windowDays)}`;
                                    }
                                    setMessage({ type: "success", text: successMsg });
                                    await fetchCampaignDetails();
                                    if (blocked.length > 0) {
                                        setTimeout(() => setMessage(null), DUPLICATE_SKIP_NOTICE_DISPLAY_MS);
                                    }
                                } else if (blocked.length > 0) {
                                    setCsvError([
                                        `No new customers were added. ${formatDuplicateSkippedNotice(blocked.length, windowDays)}`,
                                    ]);
                                } else {
                                    setCsvError(["No customers were added."]);
                                }

                                // Scroll to message
                                setTimeout(() => {
                                    if (messageRef.current) {
                                        messageRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                                    }
                                }, 150);
                                const csvModalCloseMs =
                                    blocked.length > 0 && response.inserted_count === 0
                                        ? DUPLICATE_SKIP_NOTICE_DISPLAY_MS
                                        : 2000;
                                setTimeout(() => {
                                    setShowCSVModal(false);
                                    clearCSVInputAndErrors();
                                }, csvModalCloseMs);
                            } else {
                                const { formErrors, nestedCustomerErrors } = transformLaravelErrors(response.errors);
                                setCustomersErrors(nestedCustomerErrors);
                                setTimeout(() => setCsvError(""), 5000);
                            }
                        } catch (error) {
                            setCsvError([error.message || "Network error occurred"]);
                            setTimeout(() => setCsvError(""), 5000);
                        }
                    } else {
                        setCsvError(["No valid customers found in CSV file."]);
                        setTimeout(() => setCsvError(""), 5000);
                    }

                    setCsvProcessing(false);
                } catch (error) {
                    console.error("Error processing CSV:", error);
                    setCsvProcessing(false);
                    autoCloseModalOnError();
                }
            }
        });
    };

    const dayOrder = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
    ];
    const leftOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday'];
    const rightOrder = ['Friday', 'Saturday', 'Sunday'];
    const sortedDays = campaign?.day_timeframes
        ? Object.entries(campaign.day_timeframes).sort(
              ([dayA], [dayB]) => dayOrder.indexOf(dayA) - dayOrder.indexOf(dayB)
          )
        : [];
    const leftRows = sortedDays.filter(([d]) => leftOrder.includes(d));
    const rightRows = sortedDays.filter(([d]) => rightOrder.includes(d));

    const renderChip = (range, idx) => (
        <span key={idx} className="campaign-details-timeframes-chip">
            {range.start_time && range.end_time ? (
                <>
                    {formatScheduleTime12h(range.start_time)} -{' '}
                    {formatScheduleTime12h(range.end_time)}
                </>
            ) : (
                <span className="text-danger font-italic">Incomplete</span>
            )}
        </span>
    );

    const renderRow = ([day, timeframes]) => (
        <div key={day} className="campaign-details-timeframes-split__row">
            <div className="campaign-details-timeframes-split__day">{day}</div>
            <div className="campaign-details-timeframes-split__chips">
                {Array.isArray(timeframes) && timeframes.length > 0
                    ? timeframes.map((range, idx) => renderChip(range, idx))
                    : null}
            </div>
        </div>
    );


    const handleViewCustomer = (customer) => {
        setSelectedCustomer(customer);
        setShowCustomerDetailsModal(true);
    };

    const normalizePhoneForCompare = (p) => String(p || '').replace(/\D/g, '').replace(/^1/, '') || '';

    const handleOptOutChange = (customer, callerOptedOut) => {
        const phoneNorm = normalizePhoneForCompare(customer.phone_number);
        const samePhoneCount = customers.filter((c) => normalizePhoneForCompare(c.phone_number) === phoneNorm).length;

        if (callerOptedOut && samePhoneCount > 1) {
            setMessage({
                type: 'info',
                text: 'This phone number appears multiple times in the campaign. The opt-out will apply to all entries.',
            });
            setTimeout(() => setMessage(null), 5000);
        }

        if (!callerOptedOut && customer.caller_opted_out) {
            setOptOutReenableCustomer({ customer, callerOptedOut });
            setShowOptOutConfirmModal(true);
            return;
        }
        performOptOutChange(customer, callerOptedOut);
    };

    const confirmOptOutReenable = () => {
        if (optOutReenableCustomer) {
            performOptOutChange(optOutReenableCustomer.customer, optOutReenableCustomer.callerOptedOut);
            setShowOptOutConfirmModal(false);
            setOptOutReenableCustomer(null);
        }
    };

    const revertOptOutByPhone = (phoneNorm, previousValue) =>
        setCustomers((prev) =>
            prev.map((c) =>
                normalizePhoneForCompare(c.phone_number) === phoneNorm
                    ? { ...c, caller_opted_out: previousValue }
                    : c
            )
        );

    const performOptOutChange = (customer, callerOptedOut) => {
        const phoneNorm = normalizePhoneForCompare(customer.phone_number);
        const previousValue = !!customer.caller_opted_out;
        customersEditsRef.current += 1;
        setCustomers((prev) =>
            prev.map((c) =>
                normalizePhoneForCompare(c.phone_number) === phoneNorm
                    ? { ...c, caller_opted_out: !!callerOptedOut }
                    : c
            )
        );
        if (customer.id) {
            api.outbound
                .updateCampaignCustomerOptOut(customer.id, callerOptedOut)
                .then((res) => {
                    if (!res.status) {
                        setMessage({ type: 'error', text: res.message || 'Failed to update opt-out status.' });
                        setTimeout(() => setMessage(null), 3000);
                        revertOptOutByPhone(phoneNorm, previousValue);
                    }
                })
                .catch(() => {
                    setMessage({ type: 'error', text: 'Failed to update opt-out status.' });
                    setTimeout(() => setMessage(null), 3000);
                    revertOptOutByPhone(phoneNorm, previousValue);
                });
        }
    };

    const confirmBulkDelete = async () => {
        if (isCampaignCompleted(campaign?.status)) {
            setShowBulkConfirmModal(false);
            return;
        }

        const validSelectedIds = selectedContactIds.filter((id) =>
            customers.some((c) => c.id === id && contactHasServerId(c))
        );
        const contactIdsToDelete = [...new Set(validSelectedIds.map(Number))].filter(
            (id) => !Number.isNaN(id) && id > 0
        );
        if (contactIdsToDelete.length === 0) {
            setShowBulkConfirmModal(false);
            setMessage({
                type: 'error',
                text: 'No valid contacts selected.',
            });
            setTimeout(() => setMessage(null), 5000);
            return;
        }
        setLoading(true);

        const scrollToMessage = () => {
            setTimeout(() => {
                if (messageRef.current) {
                    messageRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                    });
                }
            }, 150);
        };

        try {
            const res = await api.outbound.bulkDeleteCampaignCustomers(campaign_id, contactIdsToDelete);
            const deletedCount =
                res?.data?.deleted_count ??
                res?.deleted_count ??
                (res?.status ? contactIdsToDelete.length : 0);

            await fetchCampaignDetails();
            setSelectedContactIds([]);

            if (res?.status) {
                setMessage({
                    type: 'success',
                    text: res.message || `${deletedCount} contact(s) removed from this campaign.`,
                });
            } else {
                setMessage({
                    type: 'error',
                    text:
                        res?.message ||
                        (deletedCount > 0
                            ? `${deletedCount} removed; some contacts could not be deleted.`
                            : 'Could not delete the selected contacts.'),
                });
            }
            scrollToMessage();
            setTimeout(() => setMessage(null), 7000);
        } catch (err) {
            console.error('Bulk delete error:', err);
            setMessage({
                type: 'error',
                text: err.response?.data?.message || err.message || 'An error occurred while deleting contacts.',
            });
            scrollToMessage();
            setTimeout(() => setMessage(null), 7000);
        } finally {
            setShowBulkConfirmModal(false);
            setLoading(false);
        }
    };

    /**
     * Opens the Clear all dialog. The count on the page can be stale (webhooks keep adding contacts and the dialer
     * keeps dialing), so it is re-read first and the dialog shows what the server reports now.
     */
    const openClearAllConfirm = async () => {
        if (clearAllDisabled || customersRefreshing) return;
        const { ok, waiting } = await refreshCustomers();
        if (!ok) return;
        if (waiting === 0) {
            showTimedMessage('info', 'There are no waiting contacts left to clear.', 5000);
            return;
        }
        setShowClearAllConfirmModal(true);
    };

    /** Removes every not-yet-dialed contact on the server (all pages); dialed contacts keep their results. */
    const confirmClearAll = async () => {
        if (clearAllInFlightRef.current) return;
        if (isCampaignCompleted(campaign?.status)) {
            setShowClearAllConfirmModal(false);
            showTimedMessage('error', 'Completed campaigns cannot delete contacts.');
            return;
        }
        clearAllInFlightRef.current = true;
        // A refresh that started before this carries the pre-clear rows; invalidate it so it cannot bring them back.
        customersEditsRef.current += 1;
        setLoading(true);

        try {
            const res = await api.outbound.clearWaitingCampaignCustomers(campaign_id);

            const reloaded = await fetchCampaignDetails();
            setSelectedContactIds([]);

            if (res?.status) {
                const removed = res?.data?.deleted_count ?? 0;
                const done = res.message || `${removed} waiting contact(s) removed from this campaign.`;
                if (reloaded) {
                    showTimedMessage('success', done);
                } else {
                    // Do not report plain success next to a list that may still show the removed contacts.
                    showTimedMessage('error', `${done} The list could not be reloaded and may be out of date. Press Refresh.`);
                }
            } else {
                showTimedMessage('error', res?.message || 'Could not clear the waiting contacts.');
            }
        } catch (err) {
            console.error('Clear all waiting contacts error:', err);
            showTimedMessage(
                'error',
                err.response?.data?.message || err.message || 'An error occurred while clearing contacts.'
            );
        } finally {
            clearAllInFlightRef.current = false;
            setShowClearAllConfirmModal(false);
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="ui loading form"></div>
        );
    }
    return (
        <div className="campaign-details-page" style={{ maxWidth: '100%', minWidth: 0 }}>
            {/* Message Component */}
            {message && (
                <div ref={messageRef}>
                    <Message
                        className="campaign-page-message"
                        color={
                            message.type === 'success'
                                ? 'green'
                                : message.type === 'info'
                                  ? 'blue'
                                  : 'red'
                        }
                    >
                        {message?.text}
                    </Message>
                </div>
            )}

            {/* Header */}
            <div className="d-flex flex-wrap justify-content-start align-items-center mb-4 gap-2">
                {/* <h4 className="mb-0">Campaign Details</h4> */}
                <div>
                    <button
                        type="button"
                        className="btn btn-sm linear-gradient yr-submitbtn campaign-rules-ref-card__mapping-guide-btn"
                        onClick={onBackToList}
                    >
                        <i className="la la-arrow-left" style={{marginTop: "-6px"}} aria-hidden="true" />
                        Back To List
                    </button>
                    {/* <button className="btn btn-outline-primary btn-sm" onClick={() => onEditCampaign(campaign)}>Edit</button> */}
                </div>
            </div>

            {/* Campaign Info & Customers — align-items-start so right column height does not track the left */}
            <div className="row align-items-start">
                <div className="col-12 col-lg-6 mb-4">
                    <div className="card campaign-info-card campaign-info-card--ref mb-3 w-100">
                        <div className="card-body campaign-info-card__body">
                            <div className="campaign-info-card__header">
                                <i className="la la-file-text-o la-2x" aria-hidden="true"/>
                                <h2 className="campaign-info-card__heading">Campaign Info</h2>
                            </div>

                            <div className="campaign-info-card__main">
                                <div className="campaign-info-card__column">
                                    <div className="campaign-info-card__field">
                                        <span className="campaign-info-card__label">Campaign name</span>
                                        <div className="campaign-info-card__value text-break">
                                            {campaign.name || '—'}
                                        </div>
                                    </div>
                                   
                                    <div className="campaign-info-card__field">
                                        <span className="campaign-info-card__label">Send type</span>
                                        <div className="campaign-info-card__value">{getSendTypeDisplay(campaign.send_type)}</div>
                                    </div>
                                    <div className="campaign-info-card__field">
                                        <span className="campaign-info-card__label">Schedule Type</span>
                                        <div className="campaign-info-card__value">
                                            {campaign.schedule
                                                ? displayScheduleType(campaign.schedule.schedule_type)
                                                : '—'}
                                        </div>
                                    </div>
                                    <div className="campaign-info-card__field">
                                        <span className="campaign-info-card__label">Workspace name</span>
                                        <div className="campaign-info-card__value text-break">
                                            {campaign.workspace_name ? campaign.workspace_name : '—'}
                                        </div>
                                    </div>
                                    {String(campaign?.type || '').toLowerCase() === 'triggered' && (
                                        <div className="campaign-info-card__field">
                                            <span className="campaign-info-card__label">Contact type</span>
                                            <div className="campaign-info-card__value">
                                                {campaign.call_group_mode === 'webhook_inline'
                                                    ? 'Call group'
                                                    : campaign.call_group_mode === 'saved_template'
                                                      ? 'Saved template groups'
                                                      : 'Individual'}
                                            </div>
                                        </div>
                                    )}
                                    <div className="campaign-info-card__field  campaign-info-card__field--flush">
                                        <span className="campaign-info-card__label">Timezone</span>
                                        <div className="campaign-info-card__value">
                                            {campaign.schedule?.timezone || '—'}
                                        </div>
                                    </div>
                                </div>

                                <div className="campaign-info-card__column">
                                    <div className="campaign-info-card__field" style={{ marginTop : '-5px'}}>
                                        <span className="campaign-info-card__label">Campaign type</span>
                                        <div className="campaign-info-card__value">{getTypeBadge(campaign.type)}</div>
                                    </div>
                                    <div className="campaign-info-card__field">
                                        <span className="campaign-info-card__label">Status</span>
                                        <div className="campaign-info-card__value">
                                            <StatusBadge status={campaign.status} />
                                        </div>
                                    </div>
                                    <div className="campaign-info-card__field">
                                        <span className="campaign-info-card__label">Campaign category</span>
                                        <div className="campaign-info-card__value">
                                            {campaign.campaign_category ? formatStatus(campaign.campaign_category) : '—'}
                                        </div>
                                    </div>
                                    <div className="campaign-info-card__field">
                                        <span className="campaign-info-card__label">Consent type</span>
                                        <div className="campaign-info-card__value">
                                            {getConsentTypeLabel(campaign.consent_type) || '—'}
                                        </div>
                                    </div>
                                    <div className="campaign-info-card__field campaign-info-card__field--flush">
                                        <span className="campaign-info-card__label">Outbound Number</span>
                                        <div className="campaign-info-card__value">
                                            {campaign.outbound_number?.phone || '—'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {campaign.consent_type === 'other' && campaign.consent_notes && (
                                <div className="campaign-info-card__consent-notes">
                                    <div className="campaign-info-card__field campaign-info-card__field--flush">
                                        <span className="campaign-info-card__label">Consent notes</span>
                                        <div className="campaign-info-card__value text-break">{campaign.consent_notes}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Display Campaign Time Frames by Day */}
                    {hasTimeframes && (
                        <div className="mb-3">
                            <div className="card shadow-sm w-100">
                                <div className="card-body p-0">
                                    <div className="timeframes-accordion">
                                        <div
                                            className="timeframes-accordion-summary"
                                            onClick={() => setTimeframesExpanded((prev) => !prev)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setTimeframesExpanded((prev) => !prev);
                                                }
                                            }}
                                            aria-expanded={timeframesExpanded}
                                            aria-label={timeframesExpanded ? 'Collapse time frames' : 'Expand time frames'}
                                        >
                                            <h2 className="mb-0 timeframes-accordion-header">
                                                <i className="la la-clock-o la-2x" aria-hidden="true" />
                                                <span style={{ fontSize : '1.125rem', color : "#959393" }}>Campaign Time Frames by Day</span>
                                            </h2>
                                            <i
                                                className={`fa fa-chevron-down timeframes-accordion-chevron${timeframesExpanded ? ' timeframes-accordion-chevron--open' : ''}`}
                                                aria-hidden="true"
                                                style={{
                                                    fontSize: '1.25rem',
                                                    color: '#959393',
                                                    transition: 'transform 0.25s ease',
                                                    transform: timeframesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                }}
                                            />
                                        </div>
                                        {timeframesExpanded && (
                                        <div className="timeframes-accordion-content">
                                                <div className="campaign-details-timeframes-split">
                                                    <div className="campaign-details-timeframes-split__col">
                                                        {leftRows.map(renderRow)}
                                                    </div>
                                                    <div className="campaign-details-timeframes-split__col campaign-details-timeframes-split__col--divider">
                                                        {rightRows.map(renderRow)}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {campaign.schedule && (
                        <div className="mt-2">
                            <div className="row">
                                <div className="col-12 mb-3">
                                    <div className="card shadow-sm w-100 campaign-details-schedule-card">
                                        <div className="card-body p-4">
                                            <div className="d-flex align-items-center mb-4">
                                                <i className="la la-calendar la-2x mr-3" aria-hidden="true" />
                                                <h2 className="mb-0 campaign-info-card__heading">Schedule Info</h2>
                                            </div>
                                            <div className="campaign-details-schedule-card__grid">
                                                <div className="campaign-details-schedule-card__item">
                                                    <div className="small text-muted mb-1">Start date</div>
                                                    <div className="font-weight-bold text-dark text-break">
                                                        {formatScheduleDisplayDate(campaign.schedule.start_date)}
                                                    </div>
                                                </div>
                                                <div className="campaign-details-schedule-card__item">
                                                    <div className="small text-muted mb-1">Start time</div>
                                                    <div className="font-weight-bold text-dark">
                                                        {formatScheduleTime12h(campaign.schedule.start_time)}
                                                    </div>
                                                </div>
                                                <div className="campaign-details-schedule-card__item">
                                                    <div className="small text-muted mb-1">Stop time</div>
                                                    <div className="font-weight-bold text-dark">
                                                        {formatScheduleTime12h(campaign.schedule.end_time)}
                                                    </div>
                                                </div>
                                                <div className="campaign-details-schedule-card__days">
                                                    <div className="campaign-details-schedule-card__days-row">
                                                        <span className="small text-muted campaign-details-schedule-card__days-label">
                                                            Days
                                                        </span>
                                                        <div className="campaign-details-schedule-card__days-badges">
                                                            {campaign.schedule.days && campaign.schedule.days.length > 0 ? (
                                                                campaign.schedule.days.map((day, idx) => (
                                                                    <span
                                                                        key={idx}
                                                                        className="badge badge-primary text-white font-weight-bold campaign-details-schedule-card__day-badge d-inline-flex align-items-center justify-content-center"
                                                                        title={day}
                                                                    >
                                                                        {dayScheduleBadgeLabel(day)}
                                                                    </span>
                                                                ))
                                                            ) : (
                                                                <span className="text-muted small">—</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                        {campaign.webhook_url && (
                                            <div className="col-12 mb-3">
                                                <div className="card shadow-sm w-100">
                                                    <div className="card-body py-4">
                                                        <div className="d-flex flex-wrap align-items-center justify-content-between">
                                                            <div className="d-flex align-items-center min-w-0 pr-3 mb-3 mb-md-0 flex-grow-1" style={{ paddingLeft: "3px"}}>
                                                                <div className="campaign-rules-ref-card__icon" aria-hidden="true" style={{ marginTop: "-23px"}} >
                                                                    <i className="la la-link la-2x" />
                                                                </div>
                                                                <div className="min-w-0 flex-grow-1" style={{ marginLeft: 25 }}>
                                                                    <h2 className="mb-1 campaign-rules-ref-card__title">
                                                                        Webhook URL
                                                                    </h2>
                                                                    <div className="campaign-rules-ref-card__webhook-url-row mb-0">
                                                                        <div className="campaign-rules-ref-card__webhook-url-field">
                                                                            <span className="text-muted small text-break campaign-rules-ref-card__webhook-url">
                                                                                {campaign.webhook_url}
                                                                            </span>
                                                                        </div>
                                                                        <div className="campaign-rules-ref-card__webhook-url-actions">
                                                                            <button
                                                                                type="button"
                                                                                className={`campaign-rules-ref-card__webhook-copy${
                                                                                    webhookCopied
                                                                                        ? ' campaign-rules-ref-card__webhook-copy--done'
                                                                                        : ''
                                                                                }`}
                                                                                onClick={handleCopyWebhook}
                                                                                title={webhookCopied ? 'Copied' : 'Copy URL'}
                                                                                aria-label="Copy webhook URL"
                                                                            >
                                                                                <i
                                                                                    className={
                                                                                        webhookCopied
                                                                                            ? 'la la-check'
                                                                                            : 'la la-copy'
                                                                                    }
                                                                                    aria-hidden="true"
                                                                                />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm linear-gradient yr-submitbtn campaign-rules-ref-card__mapping-guide-btn"
                                                                                onClick={() => setShowWebhookInfoModal(true)}
                                                                                title="Webhook Mapping Guide"
                                                                                style={WEBHOOK_MAPPING_GUIDE_BTN_STYLE}
                                                                            >
                                                                                <span>Mapping Guide</span>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Recipient Management Section for Triggered Campaigns */}
                                        {campaign?.type === 'triggered' && campaign.call_group_mode !== 'webhook_inline' && (
                                            <div className="col-12 mb-3">
                                                <div className="card shadow-sm w-100">
                                                    <div className="card-body py-4">
                                                        <div className="d-flex flex-wrap align-items-center justify-content-between">
                                                            <div className="d-flex align-items-center min-w-0 pr-3 mb-3 mb-md-0" style={{ paddingLeft: "3px"}}>
                                                                <div className="campaign-rules-ref-card__icon" aria-hidden="true" style={{ marginTop: "-10px"}}>
                                                                    <i className="la la-users la-2x" />
                                                                </div>
                                                                <div className="min-w-0" style={{ marginLeft : 25 }}>
                                                                    <h2 className="mb-1 campaign-rules-ref-card__title">Manage Customers</h2>
                                                                    <div className="d-flex flex-wrap align-items-center justify-content-md-end">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-primary btn-sm mr-2 mb-2 mb-md-0"
                                                                    style={{
                                                                        fontSize : 14,
                                                                        fontWeight : 600,
                                                                        opacity: isCampaignCompleted(campaign?.status) ? 0.5 : 1,
                                                                        cursor: isCampaignCompleted(campaign?.status)
                                                                            ? 'not-allowed'
                                                                            : 'pointer',
                                                                    }}
                                                                    onClick={() => {
                                                                        if (!isCampaignCompleted(campaign?.status)) {
                                                                            setShowManualEntry(true);
                                                                        }
                                                                    }}
                                                                    disabled={isCampaignCompleted(campaign?.status)}
                                                                >
                                                                    Add Customer Manually
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-primary btn-sm mr-2 mb-2 mb-md-0"
                                                                    style={{
                                                                        fontSize : 14,
                                                                        fontWeight : 600,
                                                                        opacity: isCampaignCompleted(campaign?.status) ? 0.5 : 1,
                                                                        cursor: isCampaignCompleted(campaign?.status)
                                                                            ? 'not-allowed'
                                                                            : 'pointer',
                                                                    }}
                                                                    onClick={() => {
                                                                        if (!isCampaignCompleted(campaign?.status)) {
                                                                            setShowCSVModal(true);
                                                                        }
                                                                    }}
                                                                    disabled={isCampaignCompleted(campaign?.status)}
                                                                >
                                                                    Upload CSV
                                                                </button>
                                                                <CSVSampleDownloadButton
                                                                    filename="campaign_customers_sample.csv"
                                                                    headers={headers}
                                                                    sampleData={sampleData}
                                                                    className="btn btn-outline-primary btn-sm mb-2 mb-md-0"
                                                                    fontWeight={600}
                                                                />
                                                            </div>
                                                                </div>
                                                            </div>
                                                          
                                                        </div>

                                                        {showManualEntry && (
                                                            <div className="customer-manager-container mt-3">
                                                                <ManualCustomerEntry
                                                                    show={showManualEntry}
                                                                    manualCustomers={manualCustomers}
                                                                    errors={errors}
                                                                    handleChange={handleChange}
                                                                    handleAddCustomer={handleAddCustomer}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {campaign?.type === 'triggered' && campaign.call_group_mode === 'webhook_inline' && (
                                            <div className="col-12 mb-3">
                                                <Message info style={{ fontSize: 14 }}>
                                                    Recipients for call-group campaigns are added via webhook only. Use <strong>Mapping Guide</strong> for payload shape.
                                                </Message>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Webhook queue delay - Only for triggered campaigns */}
                            {campaign?.type === 'triggered' && (
                                <div className="card shadow-sm mb-3 w-100 campaign-details-triggered-rules campaign-rules-ref-card">
                                    <div className="card-body campaign-rules-ref-card__body">
                                        <div className="campaign-rules-ref-card__row">
                                            <div className="campaign-rules-ref-card__icon" aria-hidden="true">
                                                <i className="la la-hourglass-half la-2x" />
                                            </div>
                                            <div className="campaign-rules-ref-card__copy">
                                                <h2 className="campaign-rules-ref-card__title">Webhook Queue Delay</h2>
                                                <p className="campaign-rules-ref-card__desc">
                                                    Days to wait before a webhook lead enters the active call queue.
                                                    Manual and CSV contacts skip this delay.
                                                </p>
                                            </div>
                                            <div className="campaign-rules-ref-card__actions">
                                                <div className="campaign-rules-ref-card__control-row" style={{ paddingTop: 15 }}>
                                                    <span className="campaign-rules-ref-card__field-label">
                                                        Queue delay
                                                    </span>
                                                    <div className="webhook-queue-delay-block__control">
                                                        <label className="sr-only" htmlFor="rules_webhook_delay">
                                                            Webhook queue delay (days)
                                                        </label>
                                                        <input
                                                            id="rules_webhook_delay"
                                                            type="number"
                                                            min={0}
                                                            max={365}
                                                            className="form-control"
                                                            value={rulesDelayDays === '' ? '' : rulesDelayDays}
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                if (v === '') {
                                                                    setRulesDelayDays('');
                                                                    return;
                                                                }
                                                                const n = parseInt(v, 10);
                                                                if (Number.isNaN(n)) return;
                                                                setRulesDelayDays(Math.min(365, Math.max(0, n)));
                                                            }}
                                                            disabled={!canEditWebhookQueueDelay()}
                                                            style={{
                                                                width: 72,
                                                                minWidth: 72,
                                                                boxSizing: 'border-box',
                                                                fontSize: '14px',
                                                                lineHeight: 1.25,
                                                            }}
                                                        />
                                                        <span className="webhook-queue-delay-block__days">days</span>
                                                    </div>
                                                </div>
                                                <div className='campaign-setting-action-button'>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm linear-gradient yr-submitbtn"
                                                        onClick={handleSaveWebhookQueueDelay}
                                                        disabled={rulesDelaySaving || !canEditWebhookQueueDelay()}
                                                        style={CAMPAIGN_DETAILS_RULES_UPDATE_BTN_STYLE}
                                                    >
                                                        {rulesDelaySaving ? (
                                                            <>
                                                                <span
                                                                    className="spinner-border spinner-border-sm mr-2"
                                                                    role="status"
                                                                    aria-hidden="true"
                                                                />
                                                                Updating...
                                                            </>
                                                        ) : (
                                                            'Update'
                                                        )}
                                                    </button>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Voicemail Retry Settings Section - Available for all campaigns */}
                            <div className="card shadow-sm mb-3 w-100 campaign-rules-ref-card campaign-rules-ref-card--voicemail-retry">
                                <div className="card-body campaign-rules-ref-card__body">
                                    <div className="campaign-rules-ref-card__row">
                                        <div className="campaign-rules-ref-card__icon" aria-hidden="true">
                                            <i className="la la-phone-square la-2x" />
                                        </div>
                                        <div className="campaign-rules-ref-card__copy">
                                            <h2 className="campaign-rules-ref-card__title">Voicemail Retry Settings</h2>
                                            <p className="campaign-rules-ref-card__desc">
                                                Configure automatic callback attempts for voicemail responses.
                                            </p>
                                        </div>
                                        <div className="campaign-rules-ref-card__actions">
                                            <VoicemailRetrySettings
                                                formData={{
                                                    ...retrySettings,
                                                    send_type: campaign.send_type,
                                                }}
                                                onChange={handleRetrySettingsChange}
                                                errors={{}}
                                                disabled={false}
                                                chromeless
                                                hideDescription
                                            />
                                             <div className="campaign-setting-action-button">
                                            <button
                                                type="button"
                                                className="btn btn-sm linear-gradient yr-submitbtn"
                                                onClick={handleSaveRetrySettings}
                                                disabled={retrySaving}
                                                style={CAMPAIGN_DETAILS_RULES_UPDATE_BTN_STYLE}
                                            >
                                                {retrySaving ? (
                                                    <>
                                                        <span
                                                            className="spinner-border spinner-border-sm mr-2"
                                                            role="status"
                                                            aria-hidden="true"
                                                        />
                                                        Updating...
                                                    </>
                                                ) : (
                                                    'Update'
                                                )}
                                            </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Duplicate data rule — not for Send Now */}
                            {campaign?.send_type !== 'now' && (
                                <>
                                    <div className="card shadow-sm mb-3 w-100 campaign-rules-ref-card campaign-rules-ref-card--duplicate-data-rule">
                                        <div className="card-body campaign-rules-ref-card__body">
                                            <div className="campaign-rules-ref-card__row">
                                                <div className="campaign-rules-ref-card__icon" aria-hidden="true">
                                                    <i className="la la-clone la-2x" />
                                                </div>
                                                <div className="campaign-rules-ref-card__copy">
                                                    <h2 className="campaign-rules-ref-card__title">
                                                        Duplicate Data Rule
                                                    </h2>
                                                    <p className="campaign-rules-ref-card__desc">
                                                        Reject numbers that were recently added to prevent re-queuing
                                                        the same contact.
                                                    </p>
                                                </div>
                                                <div className="campaign-rules-ref-card__actions">
                                                    <DuplicateDataRuleSection
                                                        enabled={dupRuleEnabled}
                                                        days={dupRuleDays}
                                                        onEnabledChange={(v) => {
                                                            setDupRuleEnabled(v);
                                                            setDupRuleDaysError('');
                                                        }}
                                                        onDaysChange={(v) => setDupRuleDays(v)}
                                                        disabled={false}
                                                        daysError={dupRuleDaysError}
                                                        idPrefix="campaign-view-triggered-dup"
                                                        footer={false}
                                                        chromeless
                                                        hideDescription
                                                    />
                                                     <div className='campaign-setting-action-button'>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm linear-gradient yr-submitbtn"
                                                            onClick={handleSaveDuplicateDataRule}
                                                            disabled={dupRuleSaving}
                                                            style={CAMPAIGN_DETAILS_RULES_UPDATE_BTN_STYLE}
                                                        >
                                                            {dupRuleSaving ? 'Saving…' : 'Update'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {campaign?.type === 'triggered' && (
                                        <div className="card shadow-sm mb-3 w-100 campaign-rules-ref-card">
                                            <div className="card-body campaign-rules-ref-card__body">
                                                <div className="campaign-rules-ref-card__row">
                                                    <div className="campaign-rules-ref-card__icon" aria-hidden="true">
                                                        <i className="la la-sitemap la-2x" />
                                                    </div>
                                                    <div className="campaign-rules-ref-card__copy">
                                                        <h2 className="campaign-rules-ref-card__title">
                                                            Call Group Webhook
                                                        </h2>
                                                        <p className="campaign-rules-ref-card__desc">
                                                            Choose whether webhooks send a structured call group or
                                                            individual customer rows. Payload shape must match this
                                                            setting.
                                                        </p>
                                                    </div>
                                                    <div className="campaign-rules-ref-card__actions">
                                                        <CallGroupWebhookToggleSection
                                                            acceptCallGroup={callGroupWebhookAccept}
                                                            onAcceptChange={(v) => {
                                                                if (!isCampaignCompleted(campaign?.status)) {
                                                                    setCallGroupWebhookAccept(v);
                                                                }
                                                            }}
                                                            disabled={isCampaignCompleted(campaign?.status)}
                                                            idPrefix="campaign-view-call-group"
                                                            footer={false}
                                                            chromeless
                                                            hideDescription
                                                        />
                                                        <div className='campaign-setting-action-button'>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm linear-gradient yr-submitbtn"
                                                                onClick={handleSaveCallGroupWebhookMode}
                                                                disabled={
                                                                    callGroupWebhookSaving ||
                                                                    isCampaignCompleted(campaign?.status)
                                                                }
                                                                style={CAMPAIGN_DETAILS_RULES_UPDATE_BTN_STYLE}
                                                            >
                                                                {callGroupWebhookSaving ? 'Saving…' : 'Update'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                </div>

                <div className="col-12 col-lg-6 mb-4">
                    <div className="card shadow-sm w-100 d-flex flex-column">
                        <div className="card-body d-flex flex-column flex-grow-1">
                        <div className="d-flex align-items-start mb-3">
                            <div className="flex-grow-1 min-w-0">
                                <div className="d-flex align-items-center">
                                    <i className="la la-users la-2x mr-3" aria-hidden="true" />
                                    <h2 className="mb-0 campaign-info-card__heading">{showCallGroupColumns ? 'Call group contacts' : 'Customers Info'}</h2>
                                </div>
                                {showCallGroupColumns && (
                                    <p className="text-muted mb-0 mt-2 small" style={{ maxWidth: 520, lineHeight: 1.45 }}>
                                        Contacts from webhooks appear here. Escalation shows progress in the chain; outcome shows the last dial
                                        attempt (same idea as other campaigns).
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="customers-info-toolbar">
                            <form onSubmit={(e) => e.preventDefault()}>
                                <div className="kt-input-icon kt-input-icon--right header-search">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by name, phone, or date..."
                                        value={customerSearch}
                                        onChange={(e) => {
                                            setCustomerSearch(e.target.value);
                                        }}
                                        aria-label="Search customers"
                                    />
                                    <span className="kt-input-icon__icon kt-input-icon__icon--right">
                                        <span><i className="la la-search" aria-hidden="true" /></span>
                                    </span>
                                </div>
                            </form>
                            <div className="customers-info-toolbar-actions">
                                <button
                                    type="button"
                                    className="btn btn-sm customers-refresh-button"
                                    disabled={customersRefreshing}
                                    aria-busy={customersRefreshing}
                                    title="Refresh customers"
                                    aria-label="Refresh customers"
                                    onClick={refreshCustomers}
                                >
                                    <i className="fa fa-refresh" style={{ fontSize: '22px', margin: '-3px 0 0 1px' }} aria-hidden="true" />
                                </button>
                                {showBulkDelete && (
                                    <button
                                        type="button"
                                        className="btn btn-sm bulk-delete-button clear-all-button"
                                        disabled={clearAllDisabled || customersRefreshing}
                                        aria-busy={customersRefreshing}
                                        title={
                                            !bulkDeleteEnabled
                                                ? 'Completed campaigns cannot delete contacts'
                                                : waitingCount === 0
                                                    ? 'No waiting contacts to clear'
                                                    : 'Remove all contacts that have not been dialed yet'
                                        }
                                        aria-label="Clear all waiting contacts"
                                        onClick={openClearAllConfirm}
                                    >
                                        Clear all
                                    </button>
                                )}
                                {showBulkDelete && (
                                    <button
                                        type="button"
                                        className="btn btn-sm bulk-delete-button"
                                        disabled={bulkDeleteDisabled}
                                        title="Delete"
                                        aria-label="Delete"
                                        onClick={() => {
                                            if (isCampaignCompleted(campaign?.status)) return;
                                            if (!bulkDeleteEnabled) return;
                                            if (selectedContactIds.length === 0) return;
                                            setShowBulkConfirmModal(true);
                                        }}
                                    >
                                        <i className="fa fa-trash" style={{ fontSize: '25px', margin: '-5px 0px 0px 3px' }} aria-hidden="true" />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div
                            className="table-responsive campaign-listing-table"
                            style={{
                                overflowX: 'auto',
                                overflowY: 'visible',
                            }}
                        >
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        {showBulkDelete && (
                                            <th
                                                style={{
                                                    verticalAlign: 'middle',
                                                    textAlign: 'center',
                                                    width: '40px',
                                                }}
                                            >
                                                <input
                                                    ref={selectAllCheckboxRef}
                                                    type="checkbox"
                                                    checked={allBulkRowsSelectedOnPage}
                                                    onChange={toggleSelectAllBulkCheckboxes}
                                                    disabled={
                                                        bulkSelectableContacts.length === 0 ||
                                                        !bulkDeleteEnabled
                                                    }
                                                    title={
                                                        !bulkDeleteEnabled &&
                                                        isCampaignCompleted(campaign?.status)
                                                            ? 'Completed campaigns cannot delete contacts'
                                                            : 'Select all deletable contacts on this page'
                                                    }
                                                    aria-label="Select all deletable contacts on this page"
                                                />
                                            </th>
                                        )}
                                        <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '18%', fontSize: 14, fontWeight: 700 }}>Name</th>
                                        <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '18%', fontSize: 14, fontWeight: 700  }}>Phone</th>
                                        {showCallGroupColumns && (
                                            <>
                                                <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '7%', fontSize: 14, fontWeight: 700 }}>
                                                    Priority
                                                </th>
                                                <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '7%', fontSize: 14, fontWeight: 700 }}>
                                                    Required
                                                </th>
                                                <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '14%', fontSize: 14, fontWeight: 700 }}>
                                                    Group name
                                                </th>
                                                <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '11%', fontSize: 14, fontWeight: 700 }}>
                                                    Escalation
                                                </th>
                                            </>
                                        )}
                                        <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '15%', fontSize: 14, fontWeight: 700 }}>Date Added</th>
                                        <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '12%', fontSize: 14, fontWeight: 700 }}>
                                            {showCallGroupColumns ? 'Outcome' : 'Status'}
                                        </th>
                                        <th style={{ verticalAlign: 'middle', textAlign: 'left', whiteSpace: 'nowrap', width: '12%', fontSize: 14, fontWeight: 700 }}>Action</th>
                                        <th style={{ verticalAlign: 'middle', textAlign: 'left', minWidth: '130px', maxWidth: '160px', lineHeight: 1.3, width: '25%', padding: '8px 4px', fontSize: 14, fontWeight: 700 }}>
                                            <div style={{ whiteSpace: 'nowrap' }}>
                                                Caller Opted Out of All
                                            </div>
                                            <div style={{ whiteSpace: 'nowrap' }}>
                                                Calling Communication
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentCustomers.length > 0 ? (
                                        currentCustomers.map((customer, idx) => (
                                            <tr key={customer.id}>
                                                {showBulkDelete && (
                                                    <td
                                                        style={{
                                                            verticalAlign: 'middle',
                                                            textAlign: 'center',
                                                            width: '40px',
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedContactIds.includes(customer.id)}
                                                            onChange={() => toggleBulkCheckbox(customer)}
                                                            disabled={
                                                                !contactHasServerId(customer) ||
                                                                !bulkDeleteEnabled
                                                            }
                                                            title={
                                                                !bulkDeleteEnabled &&
                                                                isCampaignCompleted(campaign?.status)
                                                                    ? 'Completed campaigns cannot delete contacts'
                                                                    : !customer?.id
                                                                      ? 'Cannot select this row'
                                                                      : 'Select for bulk delete'
                                                            }
                                                            aria-label={`Select ${customer.name || 'contact'} for bulk delete`}
                                                        />
                                                    </td>
                                                )}
                                                <td style={{ verticalAlign: 'middle', textAlign: 'left' }}><strong>{customer.name}</strong></td>
                                                <td style={{ verticalAlign: 'middle', textAlign: 'left', fontFamily: 'monospace' }}>
                                                    {(() => {
                                                        const digits = String(customer.phone_number || '').replace(/\D/g, '');
                                                        const display = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits;
                                                        return display ? `+1${display}` : customer.phone_number;
                                                    })()}
                                                </td>
                                                {showCallGroupColumns && (
                                                    <>
                                                        <td style={{ verticalAlign: 'middle', textAlign: 'left', fontSize: 13 }}>
                                                            {formatCallGroupPriorityCell(customer)}
                                                        </td>
                                                        <td style={{ verticalAlign: 'middle', textAlign: 'left', fontSize: 13 }}>
                                                            {formatCallGroupRequiredCell(customer)}
                                                        </td>
                                                        <td
                                                            style={{ verticalAlign: 'middle', textAlign: 'left', fontSize: 13 }}
                                                            title={customer.call_group_name || ''}
                                                        >
                                                            {formatCallGroupNameCell(customer)}
                                                        </td>
                                                        <td style={{ verticalAlign: 'middle', textAlign: 'left', fontSize: 13 }}>
                                                            <span
                                                                style={{
                                                                    color: callGroupEscalationColor(
                                                                        customer.call_group_member_status
                                                                    ),
                                                                    fontWeight: 400,
                                                                }}
                                                            >
                                                                {formatCallGroupMemberStatus(customer.call_group_member_status)}
                                                            </span>
                                                        </td>
                                                    </>
                                                )}
                                                <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                                                    {formatDate(customer.created_at)}
                                                </td>
                                                <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                                                    <span className={executionStatusBadgeClass(customer.execution_status)}>
                                                        {formatOutcomeStatus(customer.execution_status)}
                                                    </span>
                                                </td>
                                                <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                                                    <span style={{ display: 'inline-flex', gap: '8px', justifyContent: 'flex-start' }}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-link btn-sm p-0"
                                                            onClick={() => handleViewCustomer(customer)}
                                                            title="View customer details"
                                                        >
                                                            <i className="fa fa-eye" aria-hidden="true" />
                                                        </button>
                                                    </span>
                                                </td>
                                                <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>
                                                    <div
                                                        role="switch"
                                                        aria-checked={!!customer.caller_opted_out}
                                                        aria-label={
                                                            customer.caller_opted_out
                                                                ? 'Caller opted out of all calling communication'
                                                                : 'Caller can receive calls'
                                                        }
                                                        onClick={() => {
                                                            if (!isCampaignCompleted(campaign?.status)) {
                                                                handleOptOutChange(customer, !customer.caller_opted_out);
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (!isCampaignCompleted(campaign?.status) && (e.key === 'Enter' || e.key === ' ')) {
                                                                e.preventDefault();
                                                                handleOptOutChange(customer, !customer.caller_opted_out);
                                                            }
                                                        }}
                                                        tabIndex={isCampaignCompleted(campaign?.status) ? -1 : 0}
                                                        style={{
                                                            width: 44,
                                                            height: 24,
                                                            borderRadius: 12,
                                                            backgroundColor: customer.caller_opted_out ? '#28a745' : '#adb5bd',
                                                            cursor: isCampaignCompleted(campaign?.status) ? 'not-allowed' : 'pointer',
                                                            position: 'relative',
                                                            transition: 'background-color 0.2s ease',
                                                            margin: 0,
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                position: 'absolute',
                                                                top: 2,
                                                                left: customer.caller_opted_out ? 22 : 2,
                                                                width: 20,
                                                                height: 20,
                                                                borderRadius: '50%',
                                                                backgroundColor: '#fff',
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                                                transition: 'left 0.2s ease',
                                                            }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={customersTableColSpan} className="text-start text-muted py-4">
                                                No record found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-3 pt-2 campaign-customers-footer">
                            <div className="d-flex align-items-center flex-wrap" style={{ gap: 25 }}>
                                <small className="text-muted d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
                                    <span className="mr-1">Show</span>
                                    <DatatablePageSizeSelect
                                        limit={String(customersPerPage)}
                                        onChangeLimit={(e, { value }) => {
                                            setCustomersPerPage(Number(value) || 10);
                                            setCurrentPage(1);
                                        }}
                                    />
                                    <span className="ml-1">entries</span>
                                </small>
                                <small className="text-muted">
                                    {totalCustomers === 0
                                        ? 'Showing 0 of 0'
                                        : `Showing ${startIndex + 1}-${Math.min(endIndex, totalCustomers)} of ${totalCustomers}`}
                                </small>
                            </div>
                            <Pagination
                                size="mini"
                                activePage={activePage}
                                totalPages={totalPages}
                                onPageChange={(e, { activePage }) => {
                                    // Only trigger page change if there is data
                                    if (totalCustomers > 0) {
                                        handlePageChange(e, { activePage });
                                    }
                                }}
                            />
                        </div>
                        </div>
                    </div>
                </div>



            </div>

            {/* Customer Details Modal */}
            <Modal
                show={showCustomerDetailsModal}
                maxWidth="820px"
                title="Customer Details"
                headerClassName="linear-gradient"
                onclose={() => {
                    setShowCustomerDetailsModal(false);
                    setSelectedCustomer(null);
                }}
                body={
                    <div style={{ padding: '12px 20px 20px', textAlign: 'left', minWidth: 0 }}>
                        <CustomerDetailsModalBody
                            selectedCustomer={selectedCustomer}
                            phoneDisplay={
                                selectedCustomer?.phone_number
                                    ? `+1${phoneFormat(selectedCustomer.phone_number)}`
                                    : 'N/A'
                            }
                            showEmptyDynamicVariablesMessage
                        />
                    </div>
                }
                footer={
                    <div className="modal-footer d-flex justify-content-end gap-2 ov-cd-modal-footer">
                        <button
                            type="button"
                            onClick={() => {
                                setShowCustomerDetailsModal(false);
                                setSelectedCustomer(null);
                            }}
                            className="btn linear-gradient yr-submitbtn btn-sm"
                        >
                            Close
                        </button>
                    </div>
                }
            />

            {/* CSV Upload Modal */}
            <Modal
                show={showCSVModal}
                maxWidth="600px"
                title="Upload Customers"
                onclose={() => {
                    setShowCSVModal(false);
                    setCsvFile(null);
                    setCsvError("");
                    setCsvSuccess("");
                    if (fileInputRef.current) {
                        fileInputRef.current.value = null;
                    }
                }}
                body={
                    <div className="compaign-form-detail">
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: 12, lineHeight: 1.4 }}>
                            Spaces in CSV column names are removed (e.g. &quot;Policy Number&quot; → PolicyNumber). Use <code style={{ background: '#f1f3f4', padding: '1px 4px', borderRadius: 3 }}>{'{{'}VariableName{'}}'}</code> in your workspace to insert the value during calls.
                        </div>
                        {csvSuccess && (
                            <Message
                                color="green"
                                style={{ display: csvSuccess ? "block" : "none", marginTop: 30 }}
                            >
                                {csvSuccess}
                            </Message>
                        )}
                        {csvError && (
                            <div style={{ color: 'red', marginBottom: '10px' }}>
                                {Array.isArray(csvError) ? (
                                    <div>
                                        <ul style={{ margin: 0, maxHeight: '200px', overflowY: 'auto' }}>
                                            {csvError.map((err, i) => (
                                                <li key={i} style={{ marginBottom: '4px' }}>
                                                    {typeof err === "string" ? (
                                                        err
                                                    ) : (
                                                        <>
                                                            {err.row ? `Row ${err.row}: ` : ""}
                                                            {Array.isArray(err.errors)
                                                                ? err.errors.join(", ")
                                                                : err.errors || err.messages || JSON.stringify(err)}
                                                        </>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <span>{csvError}</span>
                                )}
                            </div>
                        )}

                        {customersErrors.length > 0 && (
                            <Message color="red">
                                <Message.List>
                                    {customersErrors.map((err, i) => (
                                        <Message.Item key={i}>
                                            Row {err.index + 1}: {err.message}
                                        </Message.Item>
                                    ))}
                                </Message.List>
                            </Message>
                        )}
                        {csvProcessing && <div className="ui loading form"></div>}
                        <input
                            type="file"
                            accept=".csv"
                            ref={fileInputRef}
                            onChange={(e) => {
                                if (e.target.files?.[0]) {
                                    setCsvFile(e.target.files[0]);
                                    setCsvError("");
                                    setCsvSuccess("");
                                }
                            }}
                            className="form-control my-3"
                        />
                    </div>
                }
                footer={
                    <div className="modal-footer d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                setShowCSVModal(false);
                                clearCSVInputAndErrors();
                            }}
                            className="yr-cancelbtn linear-gradient-cancel btn-sm yr-btn-small"
                        >
                            <span>Cancel</span>
                        </button>

                        <button
                            type="button"
                            onClick={handleCSVUpload}
                            disabled={loading || csvProcessing || csvError || csvSuccess}
                            className="btn linear-gradient yr-submitbtn btn-sm"
                            style={{
                                opacity: csvProcessing ? 0.6 : 1,
                                cursor: csvProcessing ? 'not-allowed' : 'pointer'
                            }}
                        >
                             {csvProcessing ? "Processing..." : "Save"}
                        </button>
                    </div>
                }
            />

            {/* Webhook Mapping Guide Modal - Only for triggered campaigns */}
            {campaign?.type === 'triggered' && (
                <Modal
                    show={showWebhookInfoModal}
                    maxWidth="820px"
                    title="Webhook Mapping Guide"
                    onclose={() => setShowWebhookInfoModal(false)}
                    body={
                        <div style={{ padding: '10px' }}>
                            <>
                                {/* Classic individual-lead mapping (customers array) — shown for all triggered campaigns */}
                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{ marginBottom: '10px', color: '#333' }}>Overview</h4>
                                    <p style={{ color: '#666', lineHeight: '1.6' }}>
                                        When sending data to this webhook URL, you need to include the following fields in your POST request payload.
                                    </p>
                                </div>

                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{ marginBottom: '10px', color: '#333' }}>Required Fields</h4>
                                    <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                                    <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Field Name</th>
                                                    <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Type</th>
                                                    <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                    <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>name</td>
                                                    <td style={{ padding: '10px', color: '#666' }}>string</td>
                                                    <td style={{ padding: '10px', color: '#666' }}>
                                                        Customer&apos;s full name. Must be 2-50 characters. Letters, digits, spaces, dots, hyphens, apostrophes and commas (e.g. &quot;Last, First&quot;); no consecutive special characters other than a comma followed by a space.
                                                    </td>
                                                </tr>
                                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                    <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>phone_number</td>
                                                    <td style={{ padding: '10px', color: '#666' }}>string</td>
                                                    <td style={{ padding: '10px', color: '#666' }}>
                                                        Customer&apos;s phone number. Must be a valid US number containing <strong>10 digits</strong>,
                                                        with optional <strong>+1</strong>.
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{ marginBottom: '10px', color: '#333' }}>Optional Fields</h4>
                                    <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                                    <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Field Name</th>
                                                    <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Type</th>
                                                    <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                    <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>dynamic_variables</td>
                                                    <td style={{ padding: '10px', color: '#666' }}>object</td>
                                                    <td style={{ padding: '10px', color: '#666' }}>
                                                        Extra customer data as key/value pairs (for example:{' '}
                                                        <code>{"{ \"Company\": \"Acme\" }"}</code>). Send dynamic field keys without spaces (e.g.{' '}
                                                        <code>PolicyNumber</code>); if keys are sent with spaces, the backend will remove them.
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{ marginBottom: '10px', color: '#333' }}>Request Example</h4>
                                    <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                                        <div style={{ marginBottom: '10px' }}>
                                            <strong style={{ color: '#495057' }}>HTTP Method:</strong>{' '}
                                            <code style={{ backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>POST</code>
                                        </div>
                                        <div style={{ marginBottom: '10px' }}>
                                            <strong style={{ color: '#495057' }}>Content-Type:</strong>{' '}
                                            <code style={{ backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>application/json</code>
                                        </div>
                                        <div style={{ marginTop: '15px' }}>
                                            <strong style={{ color: '#495057', display: 'block', marginBottom: '8px' }}>Request Body:</strong>
                                            <pre style={{
                                                backgroundColor: '#2d2d2d',
                                                color: '#f8f8f2',
                                                padding: '15px',
                                                borderRadius: '5px',
                                                overflow: 'auto',
                                                fontSize: '13px',
                                                lineHeight: '1.5',
                                                margin: 0
                                            }}>
                                                {`{
  "customers": [
    {
      "name": "John Doe",
      "phone_number": "8258044811",
      "dynamic_variables": {
        "CompanyName": "Plumbing",
        "TimeofDay": "evening",
        "AppointmentDate": "Day after tomorrow",
        "AppointmentTime": "24 hours"
      }
    },
    {
      "name": "Jane Smith",
      "phone_number": "5551234567",
      "dynamic_variables": {
        "CompanyName": "Electrical",
        "TimeofDay": "morning",
        "AppointmentDate": "Tomorrow",
        "AppointmentTime": "10:00 AM"
      }
    }
  ]
}`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{ marginBottom: '10px', color: '#333' }}>Responses</h4>
                                    <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                                    <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Status</th>
                                                    <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Meaning</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                    <td style={{ padding: '10px', fontWeight: 'bold', color: '#28a745' }}>200</td>
                                                    <td style={{ padding: '10px', color: '#666' }}>
                                                        Accepted. Body includes <code>inserted_count</code> and <code>blocked_customers</code> (rows skipped by
                                                        duplicate or opt-out rules).
                                                    </td>
                                                </tr>
                                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                    <td style={{ padding: '10px', fontWeight: 'bold', color: '#dc3545' }}>422</td>
                                                    <td style={{ padding: '10px', color: '#666' }}>
                                                        Rejected, nothing was queued. <code>message</code> says why. When a field failed validation the
                                                        body also has <code>errors</code> keyed by field, e.g. <code>customers.0.name</code> or{' '}
                                                        <code>call_group.0.name</code>; ingest rejections (campaign mode, saved template) carry only{' '}
                                                        <code>message</code>.
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td style={{ padding: '10px', fontWeight: 'bold', color: '#dc3545' }}>404</td>
                                                    <td style={{ padding: '10px', color: '#666' }}>Webhook URL does not match an active triggered campaign.</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {campaign.call_group_mode === 'webhook_inline' && (
                                    <div
                                        style={{
                                            marginTop: '28px',
                                            paddingTop: '24px',
                                            borderTop: '2px solid #dee2e6',
                                        }}
                                    >
                                        <h4 style={{ marginBottom: '12px', color: '#333' }}>Call group payload (this campaign)</h4>
                                        <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '16px' }}>
                                            This campaign uses <strong>call group (webhook)</strong>. Each POST uses a root <code>call_group</code>{' '}
                                            array instead of <code>customers</code> — that defines <strong>one</strong> escalation batch. Sending
                                            another POST later creates <strong>another</strong> independent call group (multiple groups over time). Do
                                            not send <code>customers</code> and <code>call_group</code> in the same request. Within one batch,
                                            contacts dial in <strong>ascending priority</strong> (lower numbers first; e.g. 1, then 2, then 3).
                                            Optionally set root <code>call_group_name</code> (string, max 255) so this batch is labeled in the UI.
                                        </p>
                                        <div style={{ marginBottom: '20px' }}>
                                            <h4 style={{ marginBottom: '10px', color: '#333' }}>Optional (root of request body)</h4>
                                            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                                            <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Field</th>
                                                            <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Type</th>
                                                            <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Description</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                            <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>call_group_name</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>string</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>
                                                                Optional label for this batch (shown in campaign contact list). Max 255 characters.
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <div style={{ marginBottom: '20px' }}>
                                            <h4 style={{ marginBottom: '10px', color: '#333' }}>Required per <code>call_group[]</code> item</h4>
                                            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                                            <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Field</th>
                                                            <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Type</th>
                                                            <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Description</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                            <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>name</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>string</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>
                                                                Contact name. 2–50 characters; letters, digits, spaces, dots, hyphens, apostrophes, commas (e.g. &quot;Last, First&quot;).
                                                            </td>
                                                        </tr>
                                                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                            <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>phone_number</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>string</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>
                                                                US number: <strong>10 digits</strong>, optional <strong>+1</strong>.
                                                            </td>
                                                        </tr>
                                                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                            <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>priority</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>integer</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>
                                                                Dial order: <strong>lower numbers first</strong>. Range 1–9999.
                                                            </td>
                                                        </tr>
                                                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                            <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>required</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>boolean</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>
                                                                Required in the escalation chain (<code>true</code> or <code>false</code>).
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <div style={{ marginBottom: '20px' }}>
                                            <h4 style={{ marginBottom: '10px', color: '#333' }}>Optional per call group item</h4>
                                            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                                            <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Field</th>
                                                            <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Type</th>
                                                            <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Description</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                            <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>dynamic_variables</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>object</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>Same as individual leads.</td>
                                                        </tr>
                                                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                            <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>caller_opted_out</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>boolean</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>Optional when applicable.</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <div style={{ marginBottom: '8px' }}>
                                            <h4 style={{ marginBottom: '10px', color: '#333' }}>Call group request example</h4>
                                            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                                                <pre style={{
                                                    backgroundColor: '#2d2d2d',
                                                    color: '#f8f8f2',
                                                    padding: '15px',
                                                    borderRadius: '5px',
                                                    overflow: 'auto',
                                                    fontSize: '13px',
                                                    lineHeight: '1.5',
                                                    margin: 0
                                                }}>
                                                    {`{
  "call_group_name": "After-hours escalation",
  "call_group": [
    {
      "name": "Carson",
      "phone_number": "5551112222",
      "priority": 1,
      "required": true,
      "dynamic_variables": { "AlarmType": "Burglary" }
    },
    {
      "name": "Daniel",
      "phone_number": "5553334444",
      "priority": 2,
      "required": true
    },
    {
      "name": "Garth",
      "phone_number": "5555551212",
      "priority": 3,
      "required": true
    }
  ]
}`}
                                                </pre>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {campaign.call_group_mode === 'saved_template' && (
                                    <div
                                        style={{
                                            marginTop: '28px',
                                            paddingTop: '24px',
                                            borderTop: '2px solid #dee2e6',
                                        }}
                                    >
                                        <h4 style={{ marginBottom: '12px', color: '#333' }}>Saved template payload (this campaign)</h4>
                                        <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '16px' }}>
                                            The campaign can have <strong>multiple saved call groups</strong>. Set <code>use_saved_call_group</code> to{' '}
                                            <code>true</code>. Pass <code>saved_template_id</code> or <code>saved_template_name</code> to choose which
                                            group this webhook uses; if omitted, the campaign default template applies when configured. Each POST can
                                            still create a new dial batch from that template.
                                        </p>
                                        <div style={{ marginBottom: '20px' }}>
                                            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', marginBottom: '15px' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                                            <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Field</th>
                                                            <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Type</th>
                                                            <th style={{ padding: '10px', textAlign: 'left', color: '#495057' }}>Description</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                            <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>use_saved_call_group</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>boolean</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>Must be <code>true</code>.</td>
                                                        </tr>
                                                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                            <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>saved_template_id</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>integer</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>Optional.</td>
                                                        </tr>
                                                        <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                                            <td style={{ padding: '10px', fontWeight: 'bold', color: '#007bff' }}>saved_template_name</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>string</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>Optional.</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                                            <strong style={{ color: '#495057', display: 'block', marginBottom: '8px' }}>Example</strong>
                                            <pre style={{
                                                backgroundColor: '#2d2d2d',
                                                color: '#f8f8f2',
                                                padding: '15px',
                                                borderRadius: '5px',
                                                overflow: 'auto',
                                                fontSize: '13px',
                                                lineHeight: '1.5',
                                                margin: 0
                                            }}>
                                                {`{
  "use_saved_call_group": true,
  "saved_template_id": 1
}`}
                                            </pre>
                                        </div>
                                    </div>
                                )}
                            </>
                        </div>
                    }

                    footer={
                        <div className="modal-footer d-flex justify-content-end gap-2">
                            <button
                                type="button"
                                onClick={() => setShowWebhookInfoModal(false)}
                                className="btn linear-gradient yr-submitbtn btn-sm"
                            >
                                Close
                            </button>
                        </div>
                    }
                />
            )}

            <ConfirmationModal
                open={showBulkConfirmModal}
                onClose={() => setShowBulkConfirmModal(false)}
                onConfirm={confirmBulkDelete}
                title="Delete selected contacts"
                content={`You are about to permanently delete ${selectedContactIds.length} contact(s) from this campaign. This cannot be undone.`}
                cancelLabel="Cancel"
                confirmLabel="Delete"
            />

            <ConfirmationModal
                open={showClearAllConfirmModal}
                onClose={() => setShowClearAllConfirmModal(false)}
                onConfirm={confirmClearAll}
                title="Clear all waiting contacts"
                content={`You are about to permanently remove the ${waitingCount} contact(s) currently waiting in this campaign, across all pages. Contacts that were already dialed, are ringing, or completed will stay with their results. The exact number removed is shown when it finishes. This cannot be undone.`}
                cancelLabel="Cancel"
                confirmLabel="Clear all"
            />

            {/* Confirmation Modal for Opt-Out Re-enable */}
            <ConfirmationModal
                open={showOptOutConfirmModal}
                onClose={() => {
                    setShowOptOutConfirmModal(false);
                    setOptOutReenableCustomer(null);
                }}
                onConfirm={confirmOptOutReenable}
                title="Remove from Internal DNC?"
                content="This caller previously opted out of all calling communication. Are you sure you want to remove this number from the Internal DNC list and allow outbound calls again?"
                cancelLabel="Cancel"
                confirmLabel="Confirm"
            />
        </div>


    );
};

export default CampaignDetails; 
